import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { ApiError } from '../../api/client'
import { fastDispatchQuery, saveFastDispatch, type FastDispatchSettings } from '../../api/settingsEaseedeskSmallScreens'
import { toast } from '../../ui/notify'
import { LoadFailed } from '../../ui/LoadFailed'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings → Easeedesk → Fast dispatch settings.
 *
 * Element cu element fata de `version-3/easeedesk_settings/others/
 * fast_dispatch/index.php` + `settings.php`, si gest cu gest fata de
 * `index-1.js` si `settings-1.js`: doua grupuri de randuri si un buton.
 *
 * `max_distance` e `tinyint unsigned` in baza. In portal se putea scrie orice
 * numar in casuta si MySQL il taia in tacere la 255; aici cererea o spune.
 */

const LI_STYLE: React.CSSProperties = {
  padding: '10px 20px',
  marginBottom: 4,
  borderRadius: 4,
  borderWidth: '1px 6px',
  borderColor: '#ddd',
  borderStyle: 'solid',
}

function Toggle({
  title,
  on,
  onChange,
  name,
}: {
  title: string
  on: boolean
  name: string
  onChange(next: boolean): void
}) {
  return (
    <li style={LI_STYLE}>
      <div className="row">
        <div className="col-md-6">
          <h5 className="pull-left" style={{ margin: 0, padding: '5px 0', marginLeft: 20 }}>
            {title}
          </h5>
        </div>
        <div className="col-md-6">
          <div className="pull-right">
            <input
              type="checkbox"
              name={name}
              checked={on}
              onChange={(e) => onChange(e.target.checked)}
            />
          </div>
        </div>
      </div>
    </li>
  )
}

export function FastDispatchPage() {
  const queryClient = useQueryClient()
  const page = useQuery(fastDispatchQuery())
  const [form, setForm] = useState<FastDispatchSettings | null>(null)

  useEffect(() => {
    if (page.data) setForm(page.data.settings)
  }, [page.data])

  const save = useMutation({
    mutationFn: () => saveFastDispatch(form as FastDispatchSettings),
    onSuccess: () => {
      toast('Fast dispatch settings updated.', 'Fast dispatch')
      void queryClient.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'fast_dispatch'] })
    },
    onError: (e) =>
      toast(e instanceof ApiError && e.message ? e.message : 'Something went wrong.', 'Error', 'error'),
  })

  if (page.error instanceof ApiError && page.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (page.error) {
    return <LoadFailed error={page.error} onRetry={() => void page.refetch()} what="this screen" />
  }
  if (!page.data || form === null) return <Spinner />

  const set = (patch: Partial<FastDispatchSettings>) => setForm({ ...form, ...patch })

  return (
    <div>
      <h4>
        <strong>Fast dispatch</strong> settings
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Manage settings for <strong>fast dispatch.</strong>
      </label>
      <hr />

      <section className="settings fast-dispatch-settings">
        <form
          className="fast_dispatch_settings"
          method="post"
          onSubmit={(e) => {
            e.preventDefault()
            save.mutate()
          }}
        >
          <div className="row">
            <div className="col-md-2">
              <h5 style={{ width: 300 }}>General settings</h5>
            </div>
            <div className="col-md-10">
              <ul>
                <li style={LI_STYLE}>
                  <div className="row">
                    <div className="col-md-10">
                      <h5 className="pull-left" style={{ margin: 0, padding: '5px 0', marginLeft: 20 }}>
                        <i
                          className="fas fa-info-circle color-blue font-14"
                          style={{ cursor: 'default' }}
                          title="Max distance range in kilometers to find available admins"
                        >
                          {' '}
                        </i>{' '}
                        Max distance
                      </h5>
                    </div>
                    <div className="col-md-2">
                      <input
                        type="number"
                        className="form-control"
                        name="max_distance"
                        min={0}
                        max={255}
                        value={form.max_distance}
                        onChange={(e) => set({ max_distance: Number(e.target.value) })}
                        required
                      />
                    </div>
                  </div>
                </li>
                <Toggle
                  title="Show checked-in admins"
                  name="check_in"
                  on={form.check_in === 1}
                  onChange={(v) => set({ check_in: v ? 1 : 0 })}
                />
                <Toggle
                  title="Show commuting admins"
                  name="commuting"
                  on={form.commuting === 1}
                  onChange={(v) => set({ commuting: v ? 1 : 0 })}
                />
              </ul>
            </div>
          </div>

          <hr className="dotted" />

          <div className="row">
            <div className="col-md-2">
              <h5 style={{ width: 300 }}>Admin box settings</h5>
            </div>
            <div className="col-md-10">
              <ul>
                <Toggle
                  title="Show escalate button"
                  name="escalate_button"
                  on={form.escalate_button === 1}
                  onChange={(v) => set({ escalate_button: v ? 1 : 0 })}
                />
                <Toggle
                  title="Show admin department"
                  name="admin_department"
                  on={form.admin_department === 1}
                  onChange={(v) => set({ admin_department: v ? 1 : 0 })}
                />
                <Toggle
                  title="Show admin phone"
                  name="admin_phone"
                  on={form.admin_phone === 1}
                  onChange={(v) => set({ admin_phone: v ? 1 : 0 })}
                />
              </ul>
            </div>
          </div>

          <div className="row" style={{ marginTop: 20 }}>
            <div className="col-md-12">
              <button
                className="btn btn-primary pull-right update-fast-dispatch-settings"
                type="submit"
                disabled={save.isPending}
              >
                Update
              </button>
            </div>
          </div>
        </form>
      </section>
      <hr className="dotted" />
    </div>
  )
}
